/**
 * The chat gallery (CRM chats.js openChatMediaGallery): every piece of media
 * in the loaded part of the chat, once, newest first; a card jumps to its
 * message and "More history" pages older messages in.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatherIcon } from '../components/FeatherIcon';
import { useMediaUrl } from './media';
import type { ChatMedia, ChatMessage } from './messages';

/** How many older pages one "More history" pulls in at most (loadOlderChatHistoryBatch). */
const HISTORY_PAGES = 8;

interface Props {
  accountId: string;
  messages: ChatMessage[];
  hasOlder: boolean;
  loadingOlder: boolean;
  onLoadOlder: () => void;
  onPick: (messageId: string) => void;
  onClose: () => void;
}

export function ChatGallery({ accountId, messages, hasOlder, loadingOlder, onLoadOlder, onPick, onClose }: Props) {
  const { t } = useTranslation('chat');
  const items = useMemo(() => {
    const seen = new Set<string>();
    const out: { media: ChatMedia; messageId: string }[] = [];
    for (const m of [...messages].reverse()) {
      for (const media of m.media) {
        if (!media.id || seen.has(media.id)) continue;
        seen.add(media.id);
        out.push({ media, messageId: m.id });
      }
    }
    return out;
  }, [messages]);

  // "More history": keep paging while OnlyFans has older messages, up to HISTORY_PAGES.
  const [pagesLeft, setPagesLeft] = useState(0);
  useEffect(() => {
    if (!pagesLeft || loadingOlder) return;
    if (!hasOlder) {
      setPagesLeft(0);
      return;
    }
    setPagesLeft((n) => n - 1);
    onLoadOlder();
  }, [pagesLeft, loadingOlder, hasOlder, onLoadOlder]);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);

  return (
    <div className="rc-gallery-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rc-gallery" role="dialog" aria-modal="true" aria-label={t('gallery.title')}>
        <div className="rc-gallery-head">
          <div>
            <b>{t('gallery.title')}</b>
            <span className="rc-muted">{t('gallery.meta', { media: items.length, messages: messages.length })}</span>
          </div>
          <button type="button" className={`rc-btn${pagesLeft > 0 || loadingOlder ? ' om-busy' : ''}`} disabled={!hasOlder || pagesLeft > 0} onClick={() => setPagesLeft(HISTORY_PAGES)}>
            {t('gallery.more')}
          </button>
          <button type="button" className="rc-pop-close" aria-label={t('common.close')} title={t('common.close')} onClick={onClose}>
            <FeatherIcon name="x" size={16} />
          </button>
        </div>
        {items.length ? (
          <div className="rc-gallery-grid">
            {items.map(({ media, messageId }) => (
              <GalleryCard key={media.id} accountId={accountId} media={media} onClick={() => onPick(messageId)} />
            ))}
          </div>
        ) : (
          <div className="rc-pop-empty">{t('gallery.none')}</div>
        )}
      </div>
    </div>
  );
}

function GalleryCard({ accountId, media, onClick }: { accountId: string; media: ChatMedia; onClick: () => void }) {
  const { t } = useTranslation('chat');
  const { url } = useMediaUrl(accountId, media.thumb);
  return (
    <button type="button" className="rc-gallery-card" aria-label={t('gallery.card', { id: media.id })} onClick={onClick}>
      {url ? <img src={url} alt="" loading="lazy" /> : <FeatherIcon name={media.type === 'video' ? 'video' : media.type === 'audio' ? 'mic' : 'image'} size={20} />}
      {!media.canView && (
        <span className="rc-gallery-lock">
          <FeatherIcon name="lock" size={12} />
        </span>
      )}
    </button>
  );
}
